import React, { Component } from 'react';
import { connect } from 'react-redux';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import NoSSR from '~/src/helpers/noSSR';
import { getUrlParams } from '~/src/helpers/url';
import { changeCity } from '~/src/helpers/city';
import { isClientSide } from '~/src/helpers/ssr';
import { isPlayStation } from '~/src/helpers/mobile';
import { logout } from '~/src/store/actions/auth';
import { openChat, closeChat, loadedChat } from '~/src/store/actions/chat';
import { openCitySelect, closeCitySelect } from '~/src/store/actions/citySelect';
import { getProductsInfoForMainPage } from '~/src/store/actions/main';
import { contactsListForFooter } from '~/src/store/selectors/dealers';
import { selectDomain } from '~/src/store/selectors/domain';
import { COOKIE_DOMAIN } from '~/src/constants/';
import { Footer } from './Footer';
import Header from '~/src/components/Header';
import Plank from '~/src/components/PlankMobile/Plank';
import FromLkPopUp from '~/src/components/FromLkPopUp';

const Chat = dynamic(() => import('@ertelecom/ui-react/components/chat'), { ssr: false });
const CitySelect = dynamic(() => import('~/src/components/CitySelect'), { ssr: false });

const NEW_MENU_HEIGHT = 72;
const PLANK_HEIGHT = 72;

const AppContainer = styled.div`
  width: 100%;
  background-color: ${p => p.bgColor ? p.bgColor : 'transparent'};
`;

const getMobileMarginTop = (menu, plank) => 0 + (menu && NEW_MENU_HEIGHT) + (plank && PLANK_HEIGHT) + 'px';

const Content = styled.div`
  margin-left: auto;
  position: relative;
  width: 100%;
  min-height: 100vh;
  padding-top: ${p => p.withMenu ? `${NEW_MENU_HEIGHT}px` : '0'};
  @media (max-width: 859px) {
    width: 100%;
    margin-left: 0;
    padding-top: ${p => p.withMenu ? getMobileMarginTop(p.withMenu, p.plankIsShown) : 0};
    
  }
  @media (max-width: 768px) {
      min-width: 320px;
  }
`;

export class App extends Component {
    componentDidMount() {
        this.isCallbackOpen = false;

        if (process.browser) {
            this.checkChat();

            this.isCallbackOpen = (window.location.search.indexOf('?jsGetAdjustable') !== -1 || window.location.search.indexOf('&jsGetAdjustable') !== -1);
        }
    }

    checkChat = () => {
        const url = getUrlParams(location);
        if (typeof url.openchat === 'string') {
            this.props.openChat();
        }
    };

    // callback-метод для сохранения выбранного города в CitySelect
    saveCity = (city = '') => {
        if (city.length > 0) {
            if (this.props.isAuth && city !== this.props.city.domain) {
                this.props.logout();
            }
            if (this.props.city.domain !== city) {
                changeCity(city);
            }
        }
        this.props.closeCitySelect();
    };

    chatOnload = () => {
        this.props.loadedChat(true);
    };

    closeChat = () => {
        this.props.loadedChat(false);
        this.props.closeChat();
    };

    render() {
        const {
            children,
            city,
            isAuth,
            withMenu,
            withChat,
            withFooter,
            withEmailWidget,
            emailWidgetText,
            chatIsOpen,
            openChat,
            dealersContacts,
            accessToken,
            clientPersonal,
            bgColor,
            citySelectIsOpen,
            productsInfo,
            plankIsShown,
            refreshToken,
            agreement,
            openCitySelect,
            billingDomain,
            logout,
            newMenuItems,
            enableBecomeClientPopup,
            ...props
        } = this.props;

        const thisPlayStation = isClientSide() ? isPlayStation() : false;

        return (
            <AppContainer bgColor={bgColor} {...props}>
                <NoSSR>
                    <Plank data-test='plank'/>
                </NoSSR>
                {withMenu &&
                    <Header
                        items={newMenuItems}
                        city={city}
                        openCitySelect={openCitySelect}
                        billingDomain={billingDomain}
                        cookieDomain={COOKIE_DOMAIN}
                        isAuth={isAuth}
                        logout={logout}
                        clientInfo={clientPersonal}
                        productsInfo={productsInfo}
                        plankIsShown={plankIsShown}
                        data-test='main-menu'
                        enableBecomeClientPopup={enableBecomeClientPopup}
                    />
                }
                <Content
                    withMenu={withMenu}
                    plankIsShown={plankIsShown}
                >
                    {children}

                    {withFooter &&
                        <Footer
                            dealersContacts={dealersContacts}
                            city={city}
                            menuItems={newMenuItems?.main}
                            accessToken={accessToken}
                            refreshToken={refreshToken}
                            agreement={agreement}
                            data-test='footer'
                            withEmailWidget={withEmailWidget}
                            emailWidgetText={emailWidgetText}
                        />
                    }
                </Content>

                {withChat && !thisPlayStation
                    ?   <NoSSR>
                            <Chat
                                isOpen={chatIsOpen}
                                open={openChat}
                                close={this.closeChat}
                                onLoad={this.chatOnload}
                                chatLink={'/api/consultant'}
                                data-test='chat'
                                initiator={'B2C-DOMRU'}
                            />
                        </NoSSR>
                    :   null
                }

                {citySelectIsOpen
                    ? <CitySelect
                        handleCitySave={this.saveCity}
                        cookieDomain={COOKIE_DOMAIN}
                    />
                    : null
                }

                <FromLkPopUp isAuth={isAuth} data-test='lk-popup'/>
            </AppContainer>
        );
    }
}

App.propTypes = {
    children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    dealersContacts: PropTypes.array,
    hostname: PropTypes.string,
    openChat: PropTypes.func,
    logout: PropTypes.func,
    closeChat: PropTypes.func,
    closeCitySelect: PropTypes.func,
    openCitySelect: PropTypes.func,
    city: PropTypes.object,
    isAuth: PropTypes.bool,
    citySelectIsOpen: PropTypes.bool,
    withMenu: PropTypes.bool,
    withChat: PropTypes.bool,
    withFooter: PropTypes.bool,
    chatIsOpen: PropTypes.bool,
    accessToken: PropTypes.string,
    clientPersonal: PropTypes.object,
    bgColor: PropTypes.string,
    asPath: PropTypes.string,
    enableBecomeClientPopup: PropTypes.bool,
};

App.defaultProps = {
    hostname: '',
};

const mapStateToProps = (state) => ({
    city: state.city.currentCity,
    dealersContacts: contactsListForFooter(state),
    enableBecomeClientPopup: state.menu.enableBecomeClientPopup,
    isAuth: state.auth.isAuth,
    chatIsOpen: state.chat.isOpen,
    clientPersonal: state.auth.clientPersonal,
    accessToken: state.auth.accessToken,
    citySelectIsOpen: state.citySelect.isOpen,
    plankIsShown: state.plank.show,
    productsInfo: state.main.productsInfo,
    newMenuItems: state.menu.newMenuItems,
    refreshToken: state.auth.refreshToken,
    agreement: state.auth.clientPersonal?.agreement,
    billingDomain: selectDomain(state),
});

const mapDispatchToProps = (dispatch) => ({
    openChat: () => dispatch(openChat()),
    closeChat: () => dispatch(closeChat()),
    logout: () => dispatch(logout()),
    openCitySelect: () => dispatch(openCitySelect()),
    closeCitySelect: () => dispatch(closeCitySelect()),
    loadedChat: (status) => dispatch(loadedChat(status)),
    getProductsInfo: (domain, accessToken) => dispatch(getProductsInfoForMainPage(domain, accessToken)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(App);
